import type { CloseButtonProps, DrawerV2Props, ModalProps } from '@withjoy/joykit';
import { Flex, styled, TextV2 } from '@withjoy/joykit';

export const HeaderWrapper = styled(Flex)<{ titleAlign?: 'start' | 'center' }>`
  align-items: center;
  justify-content: ${({ titleAlign }) => (titleAlign === 'center' ? 'center' : 'space-between')};
  position: relative;
  /* A caller may make the header itself a flex row to centre a fixed-height title bar. This
     wrapper is then a flex item and would shrink to its content; every other header leaves the
     Header a block, where flex is inert. */
  flex: 1;
  min-width: 0;
`;

/**
 * Groups an optional `leadingAction` (e.g. a Back icon button) with the title, so the pair moves
 * together as one flex item inside HeaderWrapper's `space-between` row -- the Close button stays
 * the only thing pinned to the far edge. `min-width: 0` lets the group (and the title inside it)
 * shrink instead of forcing the header wider than its content allows.
 */
export const HeaderLeadingGroup = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.space[4]};
  min-width: 0;
`;

/**
 * The `leadingAction` slot when the title is centred: pinned to the header's left edge and
 * vertically centred, so the title's centre stays the header's centre regardless of the action's
 * width. Mirrors how the modal's own CloseButton is pinned at the right.
 */
export const HeaderLeadingSlot = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
`;

export const DialogTitle = styled(TextV2)<{ $centered?: boolean }>`
  ${({ theme }) => theme.typography.variants.hed3};
  ${({ $centered }) => ($centered ? 'text-align: center;' : '')}

  @media (min-width: ${({ theme }) => theme.breakpoints.sm2}) {
    ${({ theme }) => theme.typography.variants.hed4};
  }
`;

export const BodyWrapper = styled.div<{ removeBodyPadding: boolean }>`
  @media (min-width: ${({ theme }) => theme.breakpoints.sm2}) {
    padding-top: ${props => (props.removeBodyPadding ? ' 0rem' : '2rem')};
  }
`;

/**
 * Minimal CloseButton override for responsive background (white on mobile, transparent on desktop).
 * Used by stays/accommodations dialogs where the close button sits over imagery.
 */
export const responsiveBgCloseButtonOverrides: NonNullable<CloseButtonProps['overrides']> = {
  Root: {
    props: {
      padding: '0px !important',
      background: { _: 'white !important', sm: 'transparent !important' },
      _focusVisible: { background: { _: 'white !important', sm: 'transparent !important' } },
      _hover: { background: { _: 'white !important', sm: 'transparent !important' } },
      _active: { background: { _: 'white !important', sm: 'transparent !important' } },
    },
  },
};

/**
 * Corner radius of the default mobile drawer's top edge. Exported so content that sits flush with
 * that edge (e.g. a full-bleed hero) can round to match instead of hardcoding the same value.
 */
export const DRAWER_TOP_RADIUS = '0.5rem';

export const drawerOverrides: NonNullable<DrawerV2Props['overrides']> = {
  Root: {
    props: {
      background: 'rgb(0 0 0 / 0.3)',
    },
  },
  Content: {
    props: {
      position: 'fixed',
      height: 'unset',
      borderRadius: `${DRAWER_TOP_RADIUS} ${DRAWER_TOP_RADIUS} 0 0`,
      width: '100vw',
      padding: '0',
      maxHeight: '100%',
    },
  },
};

export const dialogOverridesV2: NonNullable<ModalProps['overrides']> = {
  Header: {
    props: {
      position: 'absolute',
      top: 0,
      right: 0,
      backgroundColor: 'transparent',
      paddingTop: { _: '1rem !important', sm: '1rem' },
      paddingRight: { _: '1rem !important', sm: '1rem' },
    },
  },
  Body: { props: { padding: '0 !important' } },
};

export const drawerOverridesV2: NonNullable<ModalProps['overrides']> = {
  Content: { props: { borderTopLeftRadius: '12px', borderTopRightRadius: '12px', height: 'auto' } },
  Header: {
    props: {
      position: 'absolute',
      top: 0,
      right: 0,
      backgroundColor: 'transparent',
      paddingTop: '1rem !important',
      paddingRight: '1rem !important',
    },
  },
  Body: { props: { padding: '0 !important' } },
};
